import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';
const LAST_UPDATED = 'April 5, 2026';

interface Section {
  id: string;
  title: string;
  icon: string;
  content: React.ReactNode;
}

export default function RefundPolicyPage() {
  useEffect(() => {
    document.title = 'Refund & Tax Policy | EVENTSHOWCASE';

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Refund & Tax Policy - EVENTSHOWCASE',
      description: 'Everything you need to know about refunds, platform commission, and tax responsibilities on EVENTSHOWCASE.',
      url: `${SITE_URL}/legal/refund-policy`,
      dateModified: '2026-04-05',
      isPartOf: {
        '@type': 'WebSite',
        name: 'EVENTSHOWCASE',
        url: SITE_URL,
      },
    };

    const existing = document.getElementById('schema-refund-policy');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-refund-policy';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-refund-policy');
      if (el) el.remove();
    };
  }, []);

  const sections: Section[] = [
    {
      id: 'overview',
      title: 'Overview',
      icon: 'ri-file-text-line',
      content: (
        <p className="text-sm text-gray-600 leading-relaxed">
          This Refund &amp; Tax Policy governs all transactions made through EVENTSHOWCASE, including event ticket purchases and digital product purchases from seller stores. By completing a purchase on this platform, you agree to the terms outlined below.
        </p>
      ),
    },
    {
      id: 'event-refunds',
      title: 'Event Ticket Refunds',
      icon: 'ri-calendar-event-line',
      content: (
        <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
          <p>Refund eligibility for event tickets is determined by the platform&apos;s refund window policy and the individual event organizer&apos;s terms.</p>
          <div className="space-y-3">
            {[
              { label: 'Full Refund', desc: 'Available if requested within the configured refund window (typically 7 days before the event date), subject to the organizer\'s refund type setting.' },
              { label: 'Partial Refund', desc: 'Where applicable, an 80% refund may be issued if the full refund window has passed but the event has not yet occurred.' },
              { label: 'Store Credit', desc: 'Some organizers may offer store credit in lieu of a cash refund. This will be clearly communicated at the time of purchase.' },
              { label: 'No Refund', desc: 'Certain events may be marked as non-refundable. This will be displayed before you complete your purchase.' },
            ].map(item => (
              <div key={item.label} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <p className="font-semibold text-gray-800 mb-0.5">{item.label}</p>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <i className="ri-error-warning-line text-amber-600 mt-0.5 flex-shrink-0"></i>
            <p className="text-amber-800">Refunds are not available after an event has taken place, regardless of attendance. If an event is cancelled by the organizer, a full refund will be issued automatically where auto-refund is enabled.</p>
          </div>
        </div>
      ),
    },
    {
      id: 'store-refunds',
      title: 'Digital Product (Store) Refunds',
      icon: 'ri-store-3-line',
      content: (
        <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
          <p>Digital products sold through seller stores on EVENTSHOWCASE are generally <strong className="text-gray-800">non-refundable</strong> once the download link has been accessed or the file has been delivered, due to the nature of digital goods.</p>
          <div className="space-y-3">
            {[
              { label: 'Defective or Corrupted Files', desc: 'If the product file is defective, corrupted, or significantly different from its description, you may request a replacement or refund within 7 days of purchase.' },
              { label: 'Duplicate Purchase', desc: 'If you accidentally purchased the same product twice, contact support within 48 hours for a refund of the duplicate charge.' },
              { label: 'Unauthorized Purchase', desc: 'If you believe a purchase was made without your authorization, contact us immediately. We will investigate and process a refund if fraud is confirmed.' },
            ].map(item => (
              <div key={item.label} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <p className="font-semibold text-gray-800 mb-0.5">{item.label}</p>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'how-to-request',
      title: 'How to Request a Refund',
      icon: 'ri-customer-service-2-line',
      content: (
        <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
          <p>To request a refund, follow these steps:</p>
          <ol className="space-y-3">
            {[
              'Log in to your EVENTSHOWCASE account and navigate to your Dashboard.',
              'Go to My Registrations (for events) or My Orders (for store products).',
              'Locate the relevant purchase and click "Request Refund".',
              'Provide a brief reason for your refund request.',
              'Our team will review your request within 3–5 business days and respond via email.',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <p>{step}</p>
              </li>
            ))}
          </ol>
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start gap-3">
            <i className="ri-time-line text-teal-600 mt-0.5 flex-shrink-0"></i>
            <p className="text-teal-800">Approved refunds are processed within <strong>5–10 business days</strong> and returned to your original payment method.</p>
          </div>
        </div>
      ),
    },
    {
      id: 'platform-commission',
      title: 'Platform Commission',
      icon: 'ri-percent-line',
      content: (
        <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
          <p>EVENTSHOWCASE charges a platform commission on each successful transaction. This commission is:</p>
          <ul className="space-y-2">
            {[
              'Deducted from the seller\'s or organizer\'s earnings before payout — not charged to the buyer.',
              'A service fee for use of the platform, payment processing infrastructure, and seller tools.',
              'Not a tax. The commission is separate from any applicable taxes.',
              'Non-refundable in the event of a buyer refund, unless the platform determines otherwise at its sole discretion.',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <i className="ri-checkbox-circle-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: 'tax-policy',
      title: 'Tax Policy',
      icon: 'ri-scales-3-line',
      content: (
        <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-government-line text-amber-600"></i>
              </div>
              <div>
                <p className="font-semibold text-amber-900 text-base">Seller-Responsible Tax Model</p>
                <p className="text-amber-700 text-xs mt-0.5">EVENTSHOWCASE operates as a marketplace facilitator</p>
              </div>
            </div>
            <p className="text-amber-800 leading-relaxed">
              EVENTSHOWCASE <strong>does not collect, withhold, or remit taxes</strong> (including VAT, GST, sales tax, or income tax) on behalf of sellers or event organizers. Each seller and organizer is solely responsible for determining, collecting, and remitting any applicable taxes in their jurisdiction.
            </p>
          </div>

          <div className="space-y-3">
            <p className="font-semibold text-gray-800">What this means for sellers &amp; organizers:</p>
            {[
              { icon: 'ri-user-line', title: 'Your Responsibility', desc: 'You must determine whether your sales are subject to tax in your country, state, or region and comply with all applicable tax laws.' },
              { icon: 'ri-receipt-line', title: 'Keep Records', desc: 'Maintain accurate records of all earnings. Use the Earnings Statement CSV available in your dashboard for tax filing purposes.' },
              { icon: 'ri-building-line', title: 'Register if Required', desc: 'If your sales volume requires tax registration (e.g., VAT registration in the EU), you are responsible for registering and filing accordingly.' },
              { icon: 'ri-file-text-line', title: 'Acknowledgment', desc: 'By creating an account, you have acknowledged your tax responsibilities. This acknowledgment is timestamped and stored as a legal record.' },
            ].map(item => (
              <div key={item.title} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-teal-600 text-sm`}></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-0.5">{item.title}</p>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <p className="font-semibold text-gray-800">What this means for buyers:</p>
            <p>Prices displayed on EVENTSHOWCASE may or may not include applicable local taxes. If you are purchasing as a business and require a tax invoice, please contact the seller or organizer directly. EVENTSHOWCASE cannot issue tax invoices on behalf of sellers.</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500">
              <strong>Disclaimer:</strong> This policy does not constitute legal or tax advice. We strongly recommend consulting a qualified tax professional in your jurisdiction to understand your specific obligations.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'disputes',
      title: 'Disputes & Chargebacks',
      icon: 'ri-shield-line',
      content: (
        <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
          <p>We encourage buyers to contact us before initiating a chargeback with their bank or payment provider. Chargebacks can result in account suspension for sellers and additional processing fees.</p>
          <p>If you have a dispute regarding a charge, please contact our support team first. We aim to resolve all disputes fairly and promptly within 5 business days.</p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <i className="ri-error-warning-line text-red-500 mt-0.5 flex-shrink-0"></i>
            <p className="text-red-800">Fraudulent chargebacks may result in permanent account termination and legal action.</p>
          </div>
        </div>
      ),
    },
    {
      id: 'contact',
      title: 'Contact Us',
      icon: 'ri-mail-line',
      content: (
        <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
          <p>For refund requests, tax inquiries, or any questions about this policy, please reach out to us:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">Email Support</p>
                <p className="font-semibold text-gray-800">support@eventshowcase.com</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">Response Time</p>
                <p className="font-semibold text-gray-800">Within 2 business days</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-teal-100 hover:text-white text-sm mb-8 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="ri-refund-2-line text-3xl text-white"></i>
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3">Refund &amp; Tax Policy</h1>
              <p className="text-teal-100 text-base leading-relaxed max-w-2xl">
                Everything you need to know about refunds, platform commission, and tax responsibilities on EVENTSHOWCASE.
              </p>
              <p className="text-teal-200 text-sm mt-3">Last updated: {LAST_UPDATED}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sticky TOC */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-5 lg:sticky lg:top-8">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Contents</p>
              <nav className="space-y-1">
                {sections.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                  >
                    <i className={`${s.icon} text-sm flex-shrink-0`}></i>
                    <span className="truncate">{s.title}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3 space-y-8">
            {sections.map(s => (
              <section key={s.id} id={s.id} className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 scroll-mt-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${s.icon} text-teal-600 text-lg`}></i>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{s.title}</h2>
                </div>
                {s.content}
              </section>
            ))}

            {/* Footer note */}
            <div className="bg-gray-100 rounded-xl p-5 text-center">
              <p className="text-xs text-gray-500 leading-relaxed">
                This policy was last updated on <strong>{LAST_UPDATED}</strong>. EVENTSHOWCASE reserves the right to update this policy at any time. Continued use of the platform constitutes acceptance of the updated policy.
              </p>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium">Home</Link>
                <span className="text-gray-300">•</span>
                <Link to="/signup" className="text-teal-600 hover:text-teal-700 font-medium">Sign Up</Link>
                <span className="text-gray-300">•</span>
                <Link to="/dashboard/earnings" className="text-teal-600 hover:text-teal-700 font-medium">My Earnings</Link>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
